// [F-APPLYGUARD] Google reCAPTCHA Enterprise (pontszám-alapú, láthatatlan) kliens-oldali segéd a karrier
// jelentkezéshez. A site key és az action a /api/jobs/site-config 'recaptcha' blokkjából jön (a háttér
// Applyguard::siteConfigBlock-ja), a token a beküldött FormData 'recaptcha_token' mezőjében utazik, az
// értékelést a Hireify háttér végzi (library/Applyguard.php + service/Recaptcha.php).
//
// Elvek:
//  - a script egyszer töltődik (idempotens), a token MINDEN beküldési próbálkozáshoz frissen kérődik
//    (a token ~2 percig él és egyszer használható - DUPE);
//  - bármilyen hiba / időtúllépés -> üres token: a beküldés token NÉLKÜL megy tovább, a szerver dönt
//    (soft mód átenged, required mód hibaüzenettel visszaküld - a jelentkező újra tud próbálkozni);
//  - nincs site key -> semmi nem töltődik be.
export const RECAPTCHA_TOKEN_FIELD = 'recaptcha_token';
const SCRIPT_ID = 'career-recaptcha-enterprise';
const EXECUTE_TIMEOUT_MS = 8000;
let loadPromise = null;
let loadedForKey = '';
/** Igaz, ha a site-config szerint tokent kell kérni (bekapcsolt és van site key). */
export function isRecaptchaEnabled(cfg) {
    return !!cfg && cfg.enabled === true && typeof cfg.site_key === 'string' && cfg.site_key.trim() !== '';
}
/** Teszt-segéd: a betöltési állapot nullázása (jsdom-ban a script sosem tölt be ténylegesen). */
export function resetRecaptchaForTest() {
    loadPromise = null;
    loadedForKey = '';
}
/**
 * Az Enterprise script betöltése (idempotens). null, ha nincs site key, nem böngészőben futunk, vagy a
 * script nem tölthető be.
 */
export function loadRecaptcha(siteKey) {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !siteKey) {
        return Promise.resolve(null);
    }
    if (loadPromise && loadedForKey === siteKey) {
        return loadPromise;
    }
    loadedForKey = siteKey;
    loadPromise = new Promise((resolve) => {
        const finish = () => {
            // a script betöltésekor a grecaptcha.enterprise-nek még CSAK a ready-je van; az execute a belső könyvtár
            // betöltése után, a ready visszahívásakor garantált - ezért az execute-ot ott ellenőrizzük (élőben mérve)
            const g = window.grecaptcha && window.grecaptcha.enterprise;
            if (!g || typeof g.ready !== 'function') {
                resolve(null);
                return;
            }
            try {
                g.ready(() => resolve(typeof g.execute === 'function' ? g : null));
            }
            catch (err) {
                console.error('[career/recaptcha] ready hiba:', err);
                resolve(null);
            }
        };
        if (window.grecaptcha && window.grecaptcha.enterprise) {
            finish();
            return;
        }
        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            existing.addEventListener('load', finish);
            existing.addEventListener('error', () => resolve(null));
            return;
        }
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', finish);
        script.addEventListener('error', () => {
            console.error('[career/recaptcha] a Google reCAPTCHA Enterprise script nem tölthető be');
            resolve(null);
        });
        document.head.appendChild(script);
    });
    return loadPromise;
}
/**
 * Friss token a beküldéshez; üres string, ha nem elérhető (a szerver dönt). Minden hívás új tokent kér.
 * Az időkorlát a TELJES folyamatra (script-betöltés + ready + execute) vonatkozik: ha a script sosem tölt be
 * vagy a ready nem hív vissza, a beküldés akkor sem akad meg (Codex CR).
 */
export async function getRecaptchaToken(siteKey, action = 'apply', timeoutMs = EXECUTE_TIMEOUT_MS) {
    let timer;
    try {
        const token = await Promise.race([
            (async () => {
                const g = await loadRecaptcha(siteKey);
                if (!g)
                    return '';
                return g.execute(siteKey, { action });
            })(),
            new Promise((_, reject) => {
                timer = setTimeout(() => reject(new Error('recaptcha időtúllépés (betöltés/ready/execute)')), timeoutMs);
            }),
        ]);
        return typeof token === 'string' ? token : '';
    }
    catch (err) {
        console.error('[career/recaptcha] token kérés hiba:', err);
        return '';
    }
    finally {
        if (timer !== undefined)
            clearTimeout(timer);
    }
}
